import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ClientPaymentsEndpoints as Endpoints,
  type AddClientPaymentRequest,
  type AddClientPaymentResponse,
  type AddClientReturnRequest,
  type AddClientReturnResponse,
  type AddPaymentToPaymentAccountRequest,
  type AddPaymentToPaymentAccountResponse,
  type ConfirmAnonymousPaymentRequest,
  type ConfirmAnonymousPaymentResponse,
  type CorrectClientPaymentsRequest,
  type CorrectClientPaymentsResponse,
  type CreateAndPayRecordRequest,
  type CreateAndPayRecordResponse,
  type DeletePaymentRequest,
  type DeletePaymentResponse,
  type ExportToExcelRequest,
  type ExportToExcelResponse,
  type GetAnonymousPaymentStateRequest,
  type GetAnonymousPaymentStateResponse,
  type GetClientBalanceRequest,
  type GetClientBalanceResponse,
  type GetClientPaymentFiltersDataRequest,
  type GetClientPaymentFiltersDataResponse,
  type GetClientPaymentInfoRequest,
  type GetClientPaymentInfoResponse,
  type GetClientPaymentsRequest,
  type GetClientPaymentsResponse,
  type GetOperationsFiltersDataRequest,
  type GetOperationsFiltersDataResponse,
  type GetOperationsSearchResultRequest,
  type GetOperationsSearchResultResponse,
  type GetOrCreateYookassaPaymentRequest,
  type GetOrCreateYookassaPaymentResponse,
  type GetPaymentsSearchResultRequest,
  type GetPaymentsSearchResultResponse,
  type GetRecordReceiptRequest,
  type GetRecordReceiptResponse,
  type PayRecordsByClientBalanceRequest,
  type PayRecordsByClientBalanceResponse,
  type PayRecordsByClientPaymentRequest,
  type PayRecordsByClientPaymentResponse,
  type PrepayRecordRequest,
  type PrepayRecordResponse,
  type SearchOperationsRequest,
  type SearchOperationsResponse,
  type SearchPaymentsRequest,
  type SearchPaymentsResponse,
  type TopClientBalanceRequest,
  type TopClientBalanceResponse,
} from '@app/api/ClientPayments/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CLIENT_PAYMENTS_RESOURCE } from '@app/constants';

export const clientPaymentsApi = createApi({
  reducerPath: 'clientPaymentsApi2',
  baseQuery: createApiBaseQuery(CLIENT_PAYMENTS_RESOURCE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.AddClientPayment]: mutation<
      AddClientPaymentResponse,
      AddClientPaymentRequest
    >({
      query: (params) => ({
        url: Endpoints.AddClientPayment,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetClientPayments]: query<
      GetClientPaymentsResponse,
      GetClientPaymentsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientPayments,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientPaymentInfo]: query<
      GetClientPaymentInfoResponse,
      GetClientPaymentInfoRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientPaymentInfo,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordReceipt]: query<
      GetRecordReceiptResponse,
      GetRecordReceiptRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordReceipt,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientBalance]: mutation<
      GetClientBalanceResponse,
      GetClientBalanceRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetClientBalance,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.PayRecordsByClientBalance]: mutation<
      PayRecordsByClientBalanceResponse,
      PayRecordsByClientBalanceRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.PayRecordsByClientBalance,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.PayRecordsByClientPayment]: mutation<
      PayRecordsByClientPaymentResponse,
      PayRecordsByClientPaymentRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.PayRecordsByClientPayment,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetOrCreateYookassaPayment]: mutation<
      GetOrCreateYookassaPaymentResponse,
      GetOrCreateYookassaPaymentRequest
    >({
      query: (params) => ({
        url: Endpoints.GetOrCreateYookassaPayment,
        method: Methods.POST,
        params,
      }),
    }),
    // Тоже без авторизации: фронт вызывает его сразу при открытии анонимной
    // ссылки на оплату, чтобы решить — показывать форму email или редиректить
    // на уже созданный в ЮKassa платёж (us/11183).
    [Endpoints.GetAnonymousPaymentState]: query<
      GetAnonymousPaymentStateResponse,
      GetAnonymousPaymentStateRequest
    >({
      query: (params) => ({
        url: Endpoints.GetAnonymousPaymentState,
        method: Methods.GET,
        params,
      }),
    }),
    // Тоже без авторизации: клиент открывает ссылку с токеном и подтверждает
    // платёж своим email. Токен и email уходят телом, query-параметров у
    // запроса нет (us/10744).
    [Endpoints.ConfirmAnonymousPayment]: mutation<
      ConfirmAnonymousPaymentResponse,
      ConfirmAnonymousPaymentRequest
    >({
      query: (data) => ({
        url: Endpoints.ConfirmAnonymousPayment,
        method: Methods.POST,
        data,
      }),
    }),
    [Endpoints.DeletePayment]: mutation<
      DeletePaymentResponse,
      DeletePaymentRequest
    >({
      query: (params) => ({
        url: Endpoints.DeletePayment,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.SearchPayments]: mutation<
      SearchPaymentsResponse,
      SearchPaymentsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchPayments,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetPaymentsSearchResult]: query<
      GetPaymentsSearchResultResponse,
      GetPaymentsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetPaymentsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientPaymentFiltersData]: query<
      GetClientPaymentFiltersDataResponse,
      GetClientPaymentFiltersDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientPaymentFiltersData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.AddPaymentToPaymentAccount]: mutation<
      AddPaymentToPaymentAccountResponse,
      AddPaymentToPaymentAccountRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddPaymentToPaymentAccount,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetOperationsFiltersData]: query<
      GetOperationsFiltersDataResponse,
      GetOperationsFiltersDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetOperationsFiltersData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchOperations]: mutation<
      SearchOperationsResponse,
      SearchOperationsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchOperations,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetOperationsSearchResult]: query<
      GetOperationsSearchResultResponse,
      GetOperationsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetOperationsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.TopClientBalance]: mutation<
      TopClientBalanceResponse,
      TopClientBalanceRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.TopClientBalance,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.PrepayRecord]: mutation<
      PrepayRecordResponse,
      PrepayRecordRequest
    >({
      query: (params) => ({
        url: Endpoints.PrepayRecord,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.CreateAndPayRecord]: mutation<
      CreateAndPayRecordResponse,
      CreateAndPayRecordRequest
    >({
      query: (params) => ({
        url: Endpoints.CreateAndPayRecord,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.CorrectClientPayments]: mutation<
      CorrectClientPaymentsResponse,
      CorrectClientPaymentsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CorrectClientPayments,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ExportToExcel]: query<
      ExportToExcelResponse,
      ExportToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcel,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.AddClientReturn]: mutation<
      AddClientReturnResponse,
      AddClientReturnRequest
    >({
      query: (params) => ({
        url: Endpoints.AddClientReturn,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useAddClientPaymentMutation,
  useGetClientPaymentsQuery,
  useLazyGetClientPaymentsQuery,
  useGetClientPaymentInfoQuery,
  useLazyGetClientPaymentInfoQuery,
  useGetRecordReceiptQuery,
  useLazyGetRecordReceiptQuery,
  useGetClientBalanceMutation,
  usePayRecordsByClientBalanceMutation,
  usePayRecordsByClientPaymentMutation,
  useGetOrCreateYookassaPaymentMutation,
  useGetAnonymousPaymentStateQuery,
  useConfirmAnonymousPaymentMutation,
  useDeletePaymentMutation,
  useSearchPaymentsMutation,
  useGetPaymentsSearchResultQuery,
  useLazyGetPaymentsSearchResultQuery,
  useGetClientPaymentFiltersDataQuery,
  useLazyGetClientPaymentFiltersDataQuery,
  useAddPaymentToPaymentAccountMutation,
  useGetOperationsFiltersDataQuery,
  useLazyGetOperationsFiltersDataQuery,
  useSearchOperationsMutation,
  useGetOperationsSearchResultQuery,
  useLazyGetOperationsSearchResultQuery,
  useTopClientBalanceMutation,
  usePrepayRecordMutation,
  useCreateAndPayRecordMutation,
  useCorrectClientPaymentsMutation,
  useExportToExcelQuery,
  useLazyExportToExcelQuery,
  useAddClientReturnMutation,
} = clientPaymentsApi;
